import { ref, onMounted, onUnmounted } from 'vue';

/** Bootstrap `lg` — same breakpoint used across shop layout (min-width: 992px). */
export const LG_BREAKPOINT = 992;

export function useDeviceType() {
  const isTouchDevice = () => (
    'ontouchstart' in window
    || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)
    || (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
  );

  const isDesktopDevice = () => window.innerWidth >= LG_BREAKPOINT;

  return {
    LG_BREAKPOINT,
    isTouchDevice,
    isDesktopDevice,
  };
}

/**
 * Whether tooltips should use hover (vue3-popper default) vs touch alternatives
 * (long-press, tap, etc.) in the consuming component.
 *
 * prefersHoverTooltip is true only when BOTH match:
 *   • (min-width: 992px)  — Bootstrap lg, aligns with shop mobile/desktop layout
 *   • (hover: hover)      — fine pointer (mouse/trackpad); excludes most touch-primary UIs
 *
 * When false, consumers typically set ToolTip :hover="false" and drive :show manually.
 *
 * Other strategies (not used here, for reference):
 *   • Width only (<992 → long-press): simpler, but touch laptops at ≥992 still get hover on touch
 *   • isTouchDevice() only: misaligns with layout breakpoints and hybrid devices
 *
 * @returns {{ prefersHoverTooltip: import('vue').Ref<boolean> }}
 */
export function useTooltipHoverMode() {
  const prefersHoverTooltip = ref(false);
  let lgMq;
  let hoverMq;

  const sync = () => {
    prefersHoverTooltip.value = Boolean(lgMq?.matches && hoverMq?.matches);
  };

  onMounted(() => {
    lgMq = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`);
    hoverMq = window.matchMedia('(hover: hover)');
    sync();
    lgMq.addEventListener('change', sync);
    hoverMq.addEventListener('change', sync);
  });

  onUnmounted(() => {
    lgMq?.removeEventListener('change', sync);
    hoverMq?.removeEventListener('change', sync);
  });

  return { prefersHoverTooltip };
}
